import apiClient, { apiRequest, handleApiError } from "./apiClient";
import { getProviders, getProviderApis } from "./providerService";
import { getProviderApiCatalog } from "./testExecutionService";

/**
 * Workflow Service
 * Talks to the Orbit workflow API. Workflows are the org's runnable
 * automations; provider-bundled ones are discoverable by everyone but only
 * runnable when the org is connected to that provider (see `is_accessible`).
 */

/**
 * List workflows visible to this org (own + org + global + provider-bundled).
 * Each item carries is_accessible / access_reason / provider_name /
 * integration_flow for the gated "Available with <provider>" experience.
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const getWorkflows = async () =>
  apiRequest(
    () => apiClient.get("/api/v1/workflows"),
    "Failed to load workflows"
  );

/**
 * Get a single workflow incl. its latest definition (with declared inputs).
 * @param {string} workflowId
 */
export const getWorkflow = async (workflowId) =>
  apiRequest(
    () => apiClient.get(`/api/v1/workflows/${workflowId}`),
    "Failed to load workflow"
  );

/**
 * Create a new workflow from a full definition (steps/inputs/outputs). Used by
 * the AI builder: the compiled WorkflowDefinition is persisted as the org-visible
 * workflow's first version. Returns the created WorkflowDetailOut (incl. id +
 * latest_definition).
 * @param {{name: string, description?: string, visibility?: string, provider_id?: string|null, definition: Object}} payload
 */
export const createWorkflow = async (payload) =>
  apiRequest(
    () => apiClient.post("/api/v1/workflows", payload),
    "Failed to create workflow"
  );

/**
 * Start a workflow run with the given inputs. Returns { run_id, status }.
 * @param {string} workflowId
 * @param {Object} inputs - keyed by the workflow's declared input names
 */
export const startWorkflowRun = async (workflowId, inputs) =>
  apiRequest(
    () => apiClient.post(`/api/v1/workflows/${workflowId}/runs`, { inputs }),
    "Failed to start workflow run"
  );

/**
 * Fetch a run's status + result (poll this after starting a run).
 * @param {string} runId
 */
export const getWorkflowRun = async (runId) =>
  apiRequest(
    () => apiClient.get(`/api/v1/workflows/runs/${runId}`),
    "Failed to load workflow run"
  );

/**
 * List a workflow's runs, most-recent first. The Studio/Detail canvas overlays
 * the latest run's per-step status (green/red) onto the nodes.
 * @param {string} workflowId
 */
export const getWorkflowRuns = async (workflowId) =>
  apiRequest(
    () => apiClient.get(`/api/v1/workflows/${workflowId}/runs`),
    "Failed to load workflow runs"
  );

/**
 * Update workflow metadata (name / description). Editor/admin only
 * (the server also enforces this).
 * @param {string} workflowId
 * @param {{name?: string, description?: string}} payload
 */
export const updateWorkflow = async (workflowId, payload) =>
  apiRequest(
    () => apiClient.patch(`/api/v1/workflows/${workflowId}`, payload),
    "Failed to update workflow"
  );

/**
 * Publish a new version of the workflow's definition (steps/inputs/outputs).
 * @param {string} workflowId
 * @param {Object} definition - the full WorkflowDefinition
 */
export const saveWorkflowVersion = async (workflowId, definition) =>
  apiRequest(
    () =>
      apiClient.post(`/api/v1/workflows/${workflowId}/versions`, { definition }),
    "Failed to save workflow version"
  );

/**
 * Flat catalog of every endpoint this org can call (providers -> apis ->
 * catalog). Drives the Studio's endpoint picker. Best-effort: returns whatever
 * resolves. Heavy-ish (fans out), so callers should cache it.
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const getAllEndpoints = async () => {
  try {
    const provRes = await getProviders({ limit: 200, offset: 0 });
    if (!provRes.success || !Array.isArray(provRes.data)) {
      return { success: true, data: [] };
    }
    const apiResults = await Promise.all(
      provRes.data.map((p) => getProviderApis(p.provider_id))
    );
    const apiIds = [];
    apiResults.forEach((r) => {
      if (r.success && Array.isArray(r.data)) {
        r.data.forEach((a) => {
          const aid = a.provider_api_id || a.id;
          if (aid) apiIds.push(aid);
        });
      }
    });
    const catalogs = await Promise.all(
      apiIds.map((aid) => getProviderApiCatalog(aid))
    );
    const flat = [];
    catalogs.forEach((c) => {
      const endpoints = c.success && c.data ? c.data.endpoints : null;
      if (Array.isArray(endpoints)) {
        endpoints.forEach((ep) =>
          flat.push({
            endpoint_id: ep.endpoint_id,
            endpoint_name: ep.endpoint_name,
            http_method: ep.http_method,
            path: ep.path,
            api_name: c.data.api_name,
            provider_name: c.data.provider_name,
            request_schema: ep.request_schema,
            response_schema: ep.response_schema
          })
        );
      }
    });
    return { success: true, data: flat };
  } catch (e) {
    return { success: false, message: "Failed to load endpoint catalog" };
  }
};

/**
 * Test one step for real (renders the payload + calls the endpoint with this
 * org's creds). Mirrors the executor — used by the Studio's per-step Test.
 * @param {{endpoint_id: string, request_payload: Object, context: Object}} payload
 */
export const testStep = async (payload) =>
  apiRequest(
    () => apiClient.post("/api/v1/workflows/test-step", payload),
    "Test step failed"
  );

/* ------------------------------------------------------------------ */
/* Clone / export / import                                            */
/* ------------------------------------------------------------------ */

/**
 * Duplicate a workflow inside this org as a fresh v1. Same tenant, so endpoint
 * references stay valid and nothing is redacted — the copy runs exactly like
 * the original. Returns the new WorkflowDetailOut.
 * @param {string} workflowId
 * @param {{name?: string, description?: string, source_version?: number, visibility?: "personal"|"org"}} [payload]
 */
export const cloneWorkflow = async (workflowId, payload = {}) =>
  apiRequest(
    () => apiClient.post(`/api/v1/workflows/${workflowId}/clone`, payload),
    "Failed to clone workflow"
  );

/**
 * Export one workflow as a portable bundle. Literal credentials typed into a
 * step payload or an input default are stripped server-side and listed under
 * each workflow's `redactions`.
 * @param {string} workflowId
 */
export const exportWorkflow = async (workflowId) =>
  apiRequest(
    () => apiClient.get(`/api/v1/workflows/${workflowId}/export`),
    "Failed to export workflow"
  );

/**
 * Export every workflow this org owns (or just `workflowIds` when given).
 * @param {string[]} [workflowIds]
 */
export const exportAllWorkflows = async (workflowIds) => {
  const params = workflowIds?.length ? { ids: workflowIds.join(",") } : undefined;
  return apiRequest(
    () => apiClient.get("/api/v1/workflows/export", { params }),
    "Failed to export workflows"
  );
};

/**
 * Dry run an import: reports how each referenced endpoint resolves in this org
 * (exact / remapped / unresolved), name conflicts and redactions. Writes
 * nothing, so it's safe to call as soon as a file is picked.
 * @param {Object} bundle - the parsed export file
 * @param {string} [name] - rename (single-workflow bundles only)
 */
export const previewWorkflowImport = async (bundle, name) =>
  apiRequest(
    () =>
      apiClient.post("/api/v1/workflows/import/preview", { bundle, name }),
    "Failed to inspect this export file"
  );

/**
 * Import a bundle into this org. Strict and all-or-nothing: if any step's
 * endpoint can't be resolved here the server creates nothing and returns 422
 * with the full report, which we surface as `preview` alongside the message.
 * @param {Object} bundle
 * @param {{name?: string, visibility?: "personal"|"org"}} [opts]
 * @returns {Promise<{success: boolean, data?: Array, message?: string, preview?: Object}>}
 */
export const importWorkflows = async (bundle, opts = {}) => {
  try {
    const response = await apiClient.post("/api/v1/workflows/import", {
      bundle,
      ...opts
    });
    return { success: true, data: response.data };
  } catch (error) {
    // The strict-import 422 carries a structured body ({message, preview})
    // rather than a string, so it can't go through the shared handler.
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      return {
        success: false,
        message: detail.message || "This workflow cannot be imported.",
        preview: detail.preview
      };
    }
    return handleApiError(error, "Failed to import workflows");
  }
};
